import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import {
  firebaseAuth,
  getCallable,
  Kakao,
  makeCallApiParam,
} from "@/config/env";
import * as common from "@/assets/common/common";
import * as constant from "@/constants/constant";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  signInWithCustomToken,
  UserCredential,
} from "firebase/auth";
import { Member } from "@/model/member";
import { OrgUser, OrgUserBase } from "@/model/organization";
import { Terminal } from "@vicons/ionicons5";
import { USER_ROUTES } from "@/assets/route_constants";
import { useAlertDialog } from "@/composables/useAlert";

import kakao_env, { kakaoConfig } from "@/config/kakao_env";
const kakao = Kakao;

const getCallApiCallable = getCallable("callApi");
const loginUser = "loginUser";
const joinUser = "joinUser";
const existMemberCallable = getCallable("existMember");
const requestPasswordResetCallable = getCallable("requestPasswordReset");

export interface LoginForm {
  uid: string;
  email?: string;
  password: string;
  orgId?: string;
  type: "custom" | "organization" | "google" | "kakao";
  idSave?: boolean; // 아이디 저장 여부
}

export function useAuth() {
  const store = useStore();
  const router = useRouter();
  const alertDialog = useAlertDialog();

  const loginType = ref<"user" | "organization">("user");
  const form = ref<LoginForm>({
    uid: "",
    email: "",
    password: "",
    orgId: "",
    type: "custom",
    idSave: false,
  });
  const loading = ref(false);
  const showForgotPasswordModal = ref(false);
  const forgotPasswordUid = ref("");
  const forgotPasswordLoading = ref(false);

  const SAVED_USER_ID_KEY = "createxSavedUserId";
  const SAVED_ID_SAVE_CHOICE_KEY = "createxSavedIdSaveChoice";
  const SAVED_ORG_ID_KEY = "createxSavedOrgId";
  const SAVED_LOGIN_TYPE_KEY = "createxSavedLoginType";

  const isLoggedIn = computed(() => {
    return store.state.isLoggedin;
  });
  const isOrgUser = computed(() => store.state.isOrgUser);
  const isOrgAdmin = computed(() => {
    const loginInfo = common.getLocalAuth();
    if (store.state.isOrgUser && loginInfo && loginInfo.type === "admin") {
      return true;
    } else {
      return false;
    }
  });
  const loggedInUserId = computed(() => store.state.loggedInUserId);
  const loggedInUserName = computed(() => store.state.loggedInUserName);

  let unsubscribeAuthStateChanged: (() => void) | null = null;

  onMounted(async () => {
    unsubscribeAuthStateChanged = firebaseAuth.onAuthStateChanged(
      async (user) => {
        if (user) {
          const localAuthData = common.getLocalAuth();
          if (
            localAuthData &&
            user.uid &&
            (user.uid == localAuthData.uid ||
              user.uid.includes("#" + localAuthData.uid))
          ) {
            store.commit("setAuthInfo", localAuthData);
          } else {
            if (store.state.isLoggedin) {
              store.commit("setAuthInfo", {});
            }
          }
        } else {
          if (store.state.isLoggedin) {
            store.commit("setAuthInfo", {});
          } else {
            console.log("[Auth] onAuthStateChanged: User is null.");
          }
        }
      }
    );

    const idSaveChoice = localStorage.getItem(SAVED_ID_SAVE_CHOICE_KEY);
    if (idSaveChoice === "true") {
      form.value.idSave = true;
      const savedLoginType = localStorage.getItem(SAVED_LOGIN_TYPE_KEY) as
        | "user"
        | "organization"
        | null;
      const savedUid = localStorage.getItem(SAVED_USER_ID_KEY);

      if (savedUid) {
        form.value.uid = savedUid;
      }

      if (savedLoginType === "organization") {
        loginType.value = "organization";
        const savedOrgId = localStorage.getItem(SAVED_ORG_ID_KEY);
        if (savedOrgId) {
          form.value.orgId = savedOrgId;
        }
      } else {
        loginType.value = "user";
        form.value.orgId = "";
      }
    } else {
      clearSavedIds();
      form.value.idSave = false;
    }
    await checkKakaoLogin();
  });

  onUnmounted(() => {
    if (unsubscribeAuthStateChanged) {
      unsubscribeAuthStateChanged();
    }
  });

  watch(loginType, (newLoginType, oldLoginType) => {
    if (newLoginType !== oldLoginType) {
      form.value.password = "";

      if (form.value.idSave) {
        const savedUid = localStorage.getItem(SAVED_USER_ID_KEY);
        if (newLoginType === "user") {
          form.value.uid = savedUid || "";
          form.value.orgId = "";
        } else {
          form.value.uid = savedUid || "";
          const savedOrgId = localStorage.getItem(SAVED_ORG_ID_KEY);
          form.value.orgId = savedOrgId || "";
        }
      } else {
        form.value.uid = "";
        form.value.orgId = "";
      }
    }
  });

  const clearSavedIds = () => {
    localStorage.removeItem(SAVED_USER_ID_KEY);
    localStorage.removeItem(SAVED_ID_SAVE_CHOICE_KEY);
    localStorage.removeItem(SAVED_ORG_ID_KEY);
    localStorage.removeItem(SAVED_LOGIN_TYPE_KEY);
  };

  async function kakaoGetUserInfo() {
    let userInfo = undefined;
    await kakao.API.request({
      url: "/v2/user/me",
    })
      .then((response: any) => {
        userInfo = response;
      })
      .catch((error: any) => {
        console.log(error);
      });
    return userInfo;
  }

  async function checkKakaoLogin() {
    const code = router.currentRoute.value.query.code;
    if (code != undefined) {
      loading.value = true;
      const result = (await kakao_env.getToken({
        grant_type: kakaoConfig.grantType,
        client_id: kakaoConfig.restApiKey,
        redirect_uri: kakaoConfig.reDirectUri,
        code: code,
      })) as any;

      if (result.status == 200) {
        const setAccessTokenResult = await kakao.Auth.setAccessToken(
          result.data.access_token
        );

        const kakaoUserInfo: any = await kakaoGetUserInfo();
        const joinKakaoData: { joinType: string; joinData: Member } = {
          joinType: constant.joinType.KAKAO,
          joinData: {
            uid: kakaoUserInfo.id,
            email: kakaoUserInfo.kakao_account.email || "",
            name: kakaoUserInfo.properties.nickname || "이름없음",
            type: "kakao",
          },
        };
        try {
          const result = await getCallApiCallable(
            makeCallApiParam(joinUser, joinKakaoData)
          );

          if (common.evalFunctionResult(result)) {
            const item: any = common.getItemFromFunctionResult(result);
            const token: string | null = common.getToken(result);
            if (item && token) {
              const baseInfo: OrgUserBase | OrgUser = {
                ...item,
              };

              await doSignInWithCustomToken(token!, baseInfo);
            } else {
              await alertDialog("사용자 정보 조회 실패.");
            }
          } else {
            await alertDialog(
              `카카오 로그인 실패: ${common.getResultError(result)}`,
              "카카오 로그인 오류"
            );
          }
        } catch (error: any) {
          await kakao.Auth.logout();
          await signOut(firebaseAuth);
        }
      }
      loading.value = false;
    }
  }
  async function doSignInWithCustomToken(
    token: string,
    userInfo: OrgUser | OrgUserBase | Member
  ) {
    const userCredential: UserCredential = await signInWithCustomToken(
      firebaseAuth,
      token!
    );
    if (userCredential) {
      store.commit("setAuthInfo", userInfo);

      router.push({ name: USER_ROUTES.Home.Name });
    } else {
      await alertDialog("인증 토큰 발급 오류.");
      common.removeLocalAuth();

      router.push({ name: USER_ROUTES.Home.Name });
    }
  }

  async function signInWithGoogle() {
    loading.value = true;
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(firebaseAuth, provider);
      const user = result.user;
      const joinSNSData: { joinType: string; joinData: Member } = {
        joinType: constant.joinType.GOOGLE,
        joinData: {
          uid: user.uid,
          email: user.email || "",
          name: user.displayName || "이름없음",
          type: "google",
        },
      };
      const joinSNSMemberResult = await getCallApiCallable(
        makeCallApiParam(joinUser, joinSNSData)
      );

      if (common.evalFunctionResult(joinSNSMemberResult)) {
        await user.getIdToken(true);
        store.commit("setAuthInfo", joinSNSData.joinData);

        router.push({ name: USER_ROUTES.Home.Name });
      } else {
        console.error(
          "Google Sign-In Verification failed:",
          common.getResultError(joinSNSMemberResult)
        );
        await alertDialog(
          common.getResultError(joinSNSMemberResult) || "Access denied.",
          "Google 로그인 오류"
        );
        await signOut(firebaseAuth);
      }
    } catch (error: any) {
      console.error("Google Sign-In or verification error:", error);
      if (
        error.code === "functions/unauthenticated" ||
        (error.details && error.details.code === "PERMISSION_DENIED")
      ) {
        await alertDialog("접근 권한이 없습니다.");
      } else {
        await alertDialog(
          `Google 로그인 중 오류가 발생했습니다: ${error.message}`,
          "Google 로그인 오류"
        );
      }
      if (firebaseAuth.currentUser) {
        await signOut(firebaseAuth);
      }
    } finally {
      loading.value = false;
    }
  }

  async function signInWithKakao() {
    loading.value = true;

    try {
      await kakao.Auth.authorize({
        redirectUri: kakaoConfig.reDirectUri,
        prompt: "select_account",
      });
    } catch (error: any) {
      await kakao.Auth.logout();
      await signOut(firebaseAuth);
    } finally {
      loading.value = false;
    }
  }

  async function handleSubmit() {
    loading.value = true;
    try {
      if (loginType.value === "organization") {
        const orgLoginPayload: {
          loginType: string;
          loginData: Partial<OrgUserBase>;
        } = {
          loginType: constant.loginType.ORG,
          loginData: {
            orgId: form.value.orgId,
            uid: form.value.uid,
            password: form.value.password,
          },
        };
        const result: any = await getCallApiCallable(
          makeCallApiParam(loginUser, orgLoginPayload)
        );
        const item: any = common.getItemFromFunctionResult(result);
        const token: string | null = common.getToken(result);

        if (item && token) {
          const baseInfo: OrgUserBase | OrgUser = {
            ...item,
          };
          await doSignInWithCustomToken(token!, baseInfo);
        } else {
          const error = common.getResultError(result);

          if (error == "User is not exist.") {
            await alertDialog("아이디가 존재하지 않습니다.");
          } else if (error == "Password is not correct.") {
            await alertDialog("비밀번호를 확인해주세요.");
          } else {
            await alertDialog("사용자 정보 조회에 실패했습니다.");
          }
          common.removeLocalAuth();
        }
      } else {
        const userLoginPayload: {
          loginType: string;
          loginData: Partial<Member>;
        } = {
          loginType: constant.loginType.CUSTOM,
          loginData: {
            uid: form.value.uid,
            password: form.value.password,
            type: form.value.type,
          },
        };

        const result: any = await getCallApiCallable(
          makeCallApiParam(loginUser, userLoginPayload)
        );
        const item: any = common.getItemFromFunctionResult(result);
        const token: string | null = common.getToken(result);
        if (item && token) {
          const info: Member = {
            ...item,
          };
          await doSignInWithCustomToken(token!, info);
        } else {
          const error = common.getResultError(result);

          if (error == "User is not exist.") {
            await alertDialog("아이디가 존재하지 않습니다.");
          } else if (error == "Password is not correct.") {
            await alertDialog("비밀번호를 확인해주세요.");
          } else {
            await alertDialog("사용자 정보 조회에 실패했습니다.");
          }
          common.removeLocalAuth();
        }
      }
      if (isLoggedIn.value) {
        if (form.value.idSave) {
          localStorage.setItem(SAVED_USER_ID_KEY, form.value.uid);
          localStorage.setItem(SAVED_ID_SAVE_CHOICE_KEY, "true");
        } else {
          localStorage.removeItem(SAVED_USER_ID_KEY);
          localStorage.removeItem(SAVED_ID_SAVE_CHOICE_KEY);
        }
      }
    } catch (error: any) {
      console.error("Login handleSubmit error:", error);
      await alertDialog(
        `로그인 중 오류 발생: ${error.message || "알 수 없는 오류"}`,
        "로그인 오류"
      );
    } finally {
      loading.value = false;
    }
  }

  async function handleLogout(showAlert = true) {
    loading.value = true;
    try {
      await signOut(firebaseAuth);
      if (showAlert) await alertDialog("로그아웃 되었습니다.");
      if (router.currentRoute.value.path !== "/") {
        router.push({ name: USER_ROUTES.Home.Name });
      }
    } catch (error: any) {
      console.error("Logout Error:", error);
      if (showAlert)
        await alertDialog("로그아웃 중 오류가 발생했습니다.", "로그아웃 오류");
    } finally {
      loading.value = false;
    }
  }

  async function signInWithCustomUser(memberData: Member) {
    try {
      const joinData: { joinType: string; joinData: Member } = {
        joinType: constant.joinType.CUSTOM,
        joinData: memberData,
      };
      const result = await getCallApiCallable(
        makeCallApiParam(joinUser, joinData)
      );
      if (common.evalFunctionResult(result)) {
        return null;
      } else {
        return common.getResultError(result);
      }
    } catch (error: any) {
      return error;
    }
  }
  async function existMember(memberData: Member) {
    return await existMemberCallable(memberData);
  }

  function openForgotPasswordModal() {
    forgotPasswordUid.value = "";
    showForgotPasswordModal.value = true;
  }

  function closeForgotPasswordModal() {
    showForgotPasswordModal.value = false;
  }

  async function handleForgotPasswordRequest() {
    if (!forgotPasswordUid.value.trim()) {
      await alertDialog("아이디를 입력해주세요.", "알림");
      return;
    }
    forgotPasswordLoading.value = true;
    try {
      const result: any = await requestPasswordResetCallable({
        type: "custom",
        uid: forgotPasswordUid.value,
      });
      if (common.evalFunctionResult(result)) {
        await alertDialog(
          "입력하신 아이디의 이메일로 임시 비밀번호가 발송되었습니다. 로그인 후 비밀번호를 변경해주세요.",
          "성공"
        );
        closeForgotPasswordModal();
      } else {
        const errorMsg = common.getResultError(result);
        if (errorMsg === "User not found or email missing.") {
          await alertDialog(
            "사용자를 찾을 수 없거나 이메일 정보가 없습니다.",
            "오류"
          );
        } else {
          await alertDialog(
            `비밀번호 재설정 요청 중 오류 발생: ${
              errorMsg || "알 수 없는 오류"
            }`,
            "오류"
          );
        }
      }
    } catch (error: any) {
      console.error("Forgot password request error:", error);
      await alertDialog(
        `비밀번호 재설정 요청 중 오류 발생: ${
          error.message || "알 수 없는 오류"
        }`,
        "오류"
      );
    } finally {
      forgotPasswordLoading.value = false;
    }
  }

  return {
    loginType,
    form,
    loading,
    isLoggedIn,
    isOrgUser,
    isOrgAdmin,
    loggedInUserId,
    loggedInUserName,
    signInWithGoogle,
    signInWithKakao,
    signInWithCustomUser,
    handleSubmit,
    handleLogout,
    existMember,
    showForgotPasswordModal,
    forgotPasswordUid,
    forgotPasswordLoading,
    openForgotPasswordModal,
    closeForgotPasswordModal,
    handleForgotPasswordRequest,
  };
}
